import React, { useEffect, useRef, useState } from "react";
import { submitFeedback } from "./feedbackApi";
import "./ShareFeedback.scss";

const FEEDBACK_TYPES = [
  "Data Quality & Coverage",
  "User Experience & Functionality",
  "Feature Request / Enhancement",
  "Other",
];
const MAX_CHARS = 1000;

const ShareFeedback = ({ userEmail = "", userName = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState("");
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showToast, setShowToast] = useState(false);
  const toastTimerRef = useRef(null);
  const typeRef = useRef(null);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  useEffect(() => {
    if (!isTypeOpen) return undefined;
    const handleClickOutside = (event) => {
      if (typeRef.current && !typeRef.current.contains(event.target)) {
        setIsTypeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isTypeOpen]);

  const canSubmit = Boolean(feedbackType) && comments.trim().length > 0 && !isSubmitting;

  const openDialog = () => {
    setFeedbackType("");
    setIsTypeOpen(false);
    setComments("");
    setSubmitError("");
    setIsOpen(true);
  };

  const closeDialog = () => {
    if (!isSubmitting) setIsOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitError("");
    try {
      await submitFeedback({
        feedbackCategory: feedbackType,
        feedback: comments.trim(),
        userEmailID: userEmail || "",
        appName: "DBS Portal",
      });
      setIsOpen(false);
      setShowToast(true);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Feedback Cannot be Recorded");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label="Share Feedback"
        className="share-feedback-button"
        onClick={openDialog}
      >
        Share Feedback
      </button>

      {isOpen && (
        <div className="share-feedback-overlay">
          <div className="share-feedback-backdrop" onClick={closeDialog} />
          <section
            className="share-feedback-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-feedback-title"
          >
            <div className="share-feedback-dialog-header">
              <h2 id="share-feedback-title">
                Hi {userName || "there"}, please share your feedback
              </h2>
              <button
                type="button"
                aria-label="Close feedback"
                className="share-feedback-close"
                onClick={closeDialog}
                disabled={isSubmitting}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M1.5 1.5L12.5 12.5M12.5 1.5L1.5 12.5" stroke="#656579" strokeWidth="1.75" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="share-feedback-content">
                <div className="share-feedback-field share-feedback-category" ref={typeRef}>
                  <label htmlFor="share-feedback-category">Please select the feedback category</label>
                  <div className="share-feedback-select-wrapper">
                    <button
                      id="share-feedback-category"
                      type="button"
                      className="share-feedback-select"
                      aria-haspopup="listbox"
                      aria-expanded={isTypeOpen}
                      onClick={() => setIsTypeOpen((open) => !open)}
                      disabled={isSubmitting}
                    >
                      <span className={feedbackType ? "has-value" : ""}>
                        {feedbackType || "Select category"}
                      </span>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M4 6L8 10L12 6" stroke="#064372" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    {isTypeOpen && (
                      <div className="share-feedback-options" role="listbox" aria-labelledby="share-feedback-category">
                        {FEEDBACK_TYPES.map((type) => (
                          <button
                            key={type}
                            type="button"
                            role="option"
                            aria-selected={feedbackType === type}
                            className="share-feedback-option"
                            onClick={() => {
                              setFeedbackType(type);
                              setIsTypeOpen(false);
                            }}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="share-feedback-field share-feedback-comments">
                  <label htmlFor="share-feedback-comments">Share the feedback (required)</label>
                  <textarea
                    id="share-feedback-comments"
                    placeholder="Enter feedback..."
                    value={comments}
                    maxLength={MAX_CHARS}
                    onChange={(event) => setComments(event.target.value)}
                    disabled={isSubmitting}
                  />
                  <div className="share-feedback-character-count">
                    {comments.length}/{MAX_CHARS}
                  </div>
                </div>

                {submitError && <p className="share-feedback-error" role="alert">{submitError}</p>}
              </div>

              <div className="share-feedback-dialog-actions">
                <button
                  type="button"
                  className="share-feedback-cancel"
                  onClick={closeDialog}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="share-feedback-submit"
                  disabled={!canSubmit}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {showToast && (
        <div className="share-feedback-toast" role="status" aria-live="polite">
          <div className="share-feedback-toast-header">
            <svg className="share-feedback-toast-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <circle cx="10" cy="10" r="9" stroke="#168736" strokeWidth="1.4" />
              <path d="M6 10.2l2.5 2.4L14 7.2" stroke="#168736" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="share-feedback-toast-title">Feedback submitted</p>
            <button
              type="button"
              aria-label="Dismiss feedback toast"
              className="share-feedback-toast-dismiss"
              onClick={() => setShowToast(false)}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1 1L9 9M9 1L1 9" stroke="#656579" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <p className="share-feedback-toast-message">Your feedback has been submitted successfully.</p>
        </div>
      )}
    </>
  );
};

export default ShareFeedback;
